import { Injectable } from "@angular/core";
import { SnackbarActions } from "@app/root-store/actions";
import { Store } from "@ngrx/store";
import { environment } from "environments/environment";
import { User, getAuth } from "firebase/auth";
import { AsyncSubject, EMPTY, NextObserver, Observable, Subject, Subscription, interval, of, throwError } from "rxjs";
import { catchError, filter, map, tap, timeout } from "rxjs/operators";
import { WebSocketSubject, WebSocketSubjectConfig, webSocket } from "rxjs/webSocket";
import { isMobileBrowser } from "../resources/embedded-mobile-checker";
import { HttpResponseHandler } from "../services/http-response-handler";

const MAX_REQUEST_ID = 10000;

enum BroadcastEvents {
    REQUEST_ACTIVE = "REQUEST_ACTIVE",
    RESPONSE_ACTIVE = "RESPONSE_ACTIVE",
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface WebsocketResponse {
    request_type: string;
    session_id: string;
    status_code?: number;
    status_msg?: string;
    result?: any;
    metadata: {
        [key: string]: any;
    };
    headers?: any;
}

export type WebsocketRequest = WebsocketResponse;

export interface OpenRequest {
    subject: AsyncSubject<any>;
    request: WebsocketResponse;
}

interface ConnectRequest extends WebsocketRequest {
    connect_data: Record<string, unknown>;
}
/* eslint-enable @typescript-eslint/naming-convention */

@Injectable({
    providedIn: "root",
})
export class WebsocketService {
    private dbConnection$: WebSocketSubject<WebsocketResponse>;
    private sessionId: string = null;
    private openRequests: { [requestId: number]: OpenRequest } = {};
    private pingSubscription: Subscription;
    private dbConnectionSubscription: Subscription;
    private _logoutObserver = new Subject<Error>();
    public isRefreshed = false;
    public isWizardOpen = false;
    public wizardTitle = "Page";

    constructor(private store: Store) {}

    /**
     * The config object that holds the url, de/serializer methods and the closeObserver
     */
    private websocketConfig: WebSocketSubjectConfig<any> = {
        url: environment.websocketUrl,
        deserializer: ({ data }) => JSON.parse(data),
        serializer: (request: WebsocketRequest) => JSON.stringify(request),
        closeObserver: null,
    };

    /**
     * Re-open dbConnection$
     *
     * @param fbUser
     */
    public refreshConnection(fbUser: User) {
        if (fbUser) {
            fbUser
                .getIdToken(/* forceRefresh */ true)
                .then(idToken => {
                    if (idToken === null) {
                        // Somehow we never created a WS connection (setToken() was never called)
                        console.error("Cannot refresh session without token.");
                        return;
                    }

                    this.closeSession({ clearOpenRequests: false });
                    // Token refreshed successfully
                    this.setToken(idToken)
                        .pipe(
                            // When connection established, retry any open requests
                            tap(() => {
                                Object.values(this.openRequests)
                                    .filter(({ request }) => request.request_type !== "connect")
                                    .forEach(({ request }) => {
                                        this.performRequest(request);
                                    });
                            })
                        )
                        .subscribe();
                    this.isRefreshed = true;
                })
                .catch(error => {
                    console.error("Error refreshing token:", error);
                });
        } else {
            console.warn("No user is currently signed in.");
        }
    }

    /**
     * An observer for other classes to use when the closeObserver is called
     */
    get logoutObserver() {
        return this._logoutObserver;
    }

    /**
     * Initialize the closeObserver passed to the websocket
     *
     * @description Initialize the close observer used to watch for close events on the websocket.
     * If a close event is sent, we transform it into an error that is pushed to the logoutObserver
     */
    private initializeCloseObserver(): NextObserver<CloseEvent> {
        const closeObserver = {
            closed: false,
            next: (value: CloseEvent) => {
                console.log(value);
                if (!closeObserver.closed) {
                    const error = this.convertCloseEventToError(value);
                    //DDB-6454 Will not triggerSignOut if FP is in background process on mobile device
                    if (this.timeoutOnMobileBrowser(error)) {
                        this.refreshConnection(getAuth().currentUser);
                    } else {
                        this._logoutObserver.next(error);
                    }
                }
            },
            error: (err: any) => {
                closeObserver.closed = true;
                this._logoutObserver.next(err);
            },
            complete: () => {
                closeObserver.closed = true;
            },
        };

        return closeObserver;
    }

    /**
     * Convert the close event to an error for normalized handling
     *
     * @param closeEvent The closeEvent to convert to an Error
     * @returns An Error object
     */
    private convertCloseEventToError(closeEvent: CloseEvent): Error {
        // Set a default session end error
        let error = { name: "InvalidSession", message: "Session expired. Please sign in again." };

        // If the websocket closes the session or a ping is missed
        if (closeEvent.reason === "Session expired") {
            error = { name: "SessionExpired", message: closeEvent.reason };
            // If the connection is lost on the browser side
        } else if (closeEvent.code === 1006) {
            error = {
                name: "ConnectionInterrupted",
                message: "The connection was interrupted. Please sign in again.",
            };
        } else if (closeEvent.code === 4000) {
            error = null;
        }

        return error;
    }

    /**
     * Initialize the websocket connection and subscribe to it once
     *
     * @description Creates a websocket subject and subscribes to it.
     * If a close observer is not set or the existing one is closed, it will reinitialize the close observer before creating the socket.
     * The pipe will check that the response is an actual open request and handle the response or throw an error.
     */
    private initConnection() {
        if (!this.dbConnection$) {
            if (!this.websocketConfig.closeObserver || this.websocketConfig.closeObserver.closed) {
                this.websocketConfig.closeObserver = this.initializeCloseObserver();
            }

            this.dbConnection$ = webSocket(this.websocketConfig);
            this.dbConnectionSubscription = this.dbConnection$
                .pipe(
                    filter(response => this.isValidOpenResponse(response)),
                    tap(response => this.handleSocketResponse(response)),
                    catchError((err: Error) => {
                        console.log(err);
                        if (this.dbConnection$) {
                            this.websocketConfig.closeObserver.next(
                                new CloseEvent("HandlerError", { code: 1000, wasClean: false, reason: err.message })
                            );
                            this.websocketConfig.closeObserver.complete();
                        }
                        return of(null);
                    })
                )
                .subscribe();
        }
    }

    /**
     * Check that the response is a valid open request
     *
     * @param response The response to validate
     */
    private isValidOpenResponse(response: WebsocketResponse) {
        return !!response?.metadata && !!this.openRequests[response.metadata.request_id];
    }

    /**
     * Handle the websocket responses
     *
     * @description Handle the websocket response by checking its contents, then delete the open request.
     * If the status_code is greater than or equal to 400 or the result code is 23505 (an Alula non-error code with > 400 status code),
     * throw an error on the async subject. Otherwise, next the appropriate value and complete the async subject.
     * @param response The response to handle
     */
    private handleSocketResponse(response: WebsocketResponse) {
        const requestId = response.metadata.request_id;
        const theRequest = this.openRequests[requestId].subject;
        if (
            (response.status_code < 400 && (!response.result?.code || response.result.code !== "23505")) ||
            response.request_type === "legacy_fusion_encrypt" ||
            response.request_type === "send_email"
        ) {
            if (response.request_type === "connect") {
                theRequest.next(response);
            } else {
                theRequest.next(response.result);
            }
            theRequest.complete();

            delete this.openRequests[requestId];
        } else {
            theRequest.error(response.result);

            delete this.openRequests[requestId];

            if ((response.status_code === 401 && response.status_msg === "Unauthorized") || response.request_type === "ping") {
                throw new Error("Invalid session. Please sign in again.");
            }
        }
    }

    /**
     * Close the current websocket session
     *
     * @param root0
     * @param root0.clearOpenRequests
     * @description Close the current session by completing the closeObserver, cancelling the ping timeout,
     * clearing any open requests and resetting the websocket connection.
     *
     * NOTE: This method should not be called directly in this class. Should be called in conjunction with the logOut action.
     */
    public closeSession({ clearOpenRequests = true } = {}) {
        this.websocketConfig.closeObserver.complete();
        this.cancelPing();
        if (clearOpenRequests) {
            this.clearOpenRequests();
        }
        this.clearDbConnection();
        this.isRefreshed = false;
    }

    /**
     * @param token The Firebase token to send with the connect request
     * @param closeObserver The close observer to watch for socket close events
     * @param inviteCode=null The invite code from the sign up process
     * @param inviteCode
     */
    public setToken(token: string, inviteCode = null) {
        this.initConnection();

        return this.makeRequest<any>(
            /* eslint-disable @typescript-eslint/naming-convention */
            {
                request_type: "connect",
                connect_data: { token, invite_code: inviteCode },
                session_id: "",
                metadata: {},
            } as ConnectRequest,
            /* eslint-enable @typescript-eslint/naming-convention */
            false
        ).pipe(
            map(response => {
                this.sessionId = response.session_id;
                this.sendPing();
                return response.result;
            }),
            catchError(error => {
                if (!inviteCode && typeof error !== "string") {
                    const err = new Error("Failed to create a session. Please sign in again.");
                    err.name = "FailedSession";
                    throw err;
                }

                throw error;
            })
        );
    }

    /**
     * Send a ping command to the websocket every minute to keep the connection alive
     */
    private sendPing() {
        if (!this.pingSubscription) {
            this.pingSubscription = interval(60000).subscribe(() => {
                /* eslint-disable @typescript-eslint/naming-convention */
                const pingRequest = {
                    request_type: "ping",
                    session_id: "",
                    metadata: null,
                };
                /* eslint-enable @typescript-eslint/naming-convention */
                this.makeRequest(pingRequest, false)
                    .pipe(
                        catchError((error: Error) => {
                            console.log("error:", error);
                            if (error?.name === "TimeoutError") {
                                /* eslint-disable @typescript-eslint/naming-convention */
                                this.dbConnection$.error({ code: 4008, reason: "Request Timeout" });
                                /* eslint-enable @typescript-eslint/naming-convention */
                            }
                            return EMPTY;
                        })
                    )
                    .subscribe();
            });
        }
    }

    /**
     * Cancel the websocket ping
     */
    private cancelPing() {
        this.pingSubscription?.unsubscribe();
        this.pingSubscription = null;
    }

    /**
     * Generate a unique request ID and add it to the returned result
     *
     * @param request The request to generate the ID for
     * @returns WebsocketRequest
     */
    private generateRequestId(request: WebsocketRequest): WebsocketRequest {
        let reqId = Math.floor(Math.random() * MAX_REQUEST_ID); // NOSONAR

        // Check if request id is unique
        const numIds = Object.keys(this.openRequests).length;
        if (numIds === MAX_REQUEST_ID) {
            // Something went horribly wrong if we have 10000 active connecitons
            console.error("Request id is not unique", reqId);
        } else {
            // Reroll until id is unique
            // TODO: This is terrible and awful and inefficient. Selecting from a pre-shuffled sequence would be better.
            //       But why does the id need to be random in the first place? Instead of 1,2,3,...
            while (reqId in this.openRequests) {
                reqId = Math.floor(Math.random() * MAX_REQUEST_ID); // NOSONAR
            }
        }

        return {
            ...request,
            metadata: {
                ...request.metadata,
                request_id: reqId, // eslint-disable-line @typescript-eslint/naming-convention
            },
        };
    }

    /**
     * Call next on the websocket to send the request to the server
     *
     * @param request The request data to send to the websocket
     */
    private performRequest(request: WebsocketRequest) {
        request.session_id = this.sessionId;
        this.dbConnection$.next(request);

        // The user interacted with this tab so unset the "refreshed-but-idle" flag
        if (request.request_type !== "ping") {
            this.isRefreshed = false;
        }
    }

    /**
     * This method does the following before making a websocket request:
     *  1. Initializes the websocket connection
     *  2. Generates a request ID
     *  3. Creates an AsyncSubject to be returned
     *  4. Adds the AsyncSubject to the openRequests object
     *  5. Determines if a valid session is required
     *
     * If a valid session is not required or there is already a valid session, it immediately makes the request.
     * Otherwise, it does not create a request and immediately returns an empty observable.
     *
     * The AsyncSubject is what is returned. It is subscribed to by the caller and responses from the server
     * are sent to this AsyncSubject observable
     *
     * @param request The request to send to the websocket
     * @param requiresSessionId =true Whether the request require a valid websocket session
     * @param errorsToSuppress optional array of error messages to suppress in console and snackbar
     * @returns AsyncSubject
     */
    public makeRequest<O = unknown, I extends WebsocketRequest = any>(
        request: I,
        requiresSessionId = true,
        errorsToSuppress: string[] = [],
        errorsToUpdate: any = {}
    ): Observable<O | null> {
        // if a session ID is required and set or not required, make the call
        if ((requiresSessionId && this.sessionId) || !requiresSessionId) {
            const finalRequest = this.generateRequestId(request);
            const openRequest = new AsyncSubject<O>();
            this.openRequests[finalRequest.metadata.request_id] = { subject: openRequest, request: finalRequest };
            this.initConnection();
            this.performRequest(finalRequest);

            return openRequest.pipe(
                // DDB-3140 Temporarily increasing timeout until a better call can be made to roll up sensor DCO data
                timeout(request.request_type === "connect" ? 30000 : 120000),
                catchError(e => {
                    delete this.openRequests[finalRequest.metadata.request_id];
                    if (errorsToUpdate?.originalValue === e) {
                        const errMsg = errorsToUpdate?.newValue;
                        this.store.dispatch(
                            SnackbarActions.openSnackbar({
                                message: new HttpResponseHandler(null, null, errMsg, true),
                            })
                        );
                    } else if (!errorsToSuppress?.includes(e) && e === "permission denied") {
                        this.store.dispatch(
                            SnackbarActions.openSnackbar({
                                message: new HttpResponseHandler(null, null, e, true),
                            })
                        );
                    }

                    if (this.sessionId || request.request_type === "connect") {
                        return throwError(() => e);
                    }

                    // If there is no session ID (we're signed out already) do not return an error
                    return of(null);
                })
            );
        }
        console.error("Failed to send request", request);
        return of(null);
    }

    /**
     * Errors out and deletes any outstanding websocket requests
     */
    private clearOpenRequests() {
        for (const request in this.openRequests) {
            if (this.openRequests[request]) {
                this.openRequests[request]?.subject?.complete();
            }
        }
        this.openRequests = {};
    }

    /**
     * Clear the session ID, complete the DB connection, unsubscribe from it, then set it to null
     */
    private clearDbConnection() {
        this.sessionId = null;
        this.dbConnection$?.complete();
        this.dbConnectionSubscription?.unsubscribe();
        this.dbConnection$ = null;
    }
    private timeoutOnMobileBrowser(err) {
        return (err.name === "ConnectionInterrupted" || err.name === "InvalidSession") && isMobileBrowser();
    }
}
